/**
 * AdminComplianceLog — admin console tab.
 * Read-only, searchable, chronological view of every sensitive event
 * on the platform (logins, KYC, refund submissions, admin decisions,
 * payments). Records include IP + user-agent for a legally-defensible
 * audit trail.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Filter, MapPin } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const ACTION_COLORS = {
  auth: "#C9A961",
  kyc: "#2FB999",
  admin: "#F5D67A",
  payment: "#C9A961",
  razorpay: "#C9A961",
  refund_claim: "#C0392B",
  director: "#B87333",
  settlement: "#8B7333",
  compliance: "#C0392B",
};

function pillFor(action) {
  const prefix = (action || "").split(".")[0];
  return ACTION_COLORS[prefix] || "#8A8A82";
}

export function AdminComplianceLog() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (action) params.set("action", action);
      params.set("limit", "500");
      const { data } = await api.get("/admin/audit-log?" + params.toString());
      setRows(data.events || data.rows || []);
    } catch { toast.error("Could not load audit log."); }
    finally { setLoading(false); }
  }, [api, action]);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.action || "").toLowerCase().includes(s)
        || (r.actor_email || "").toLowerCase().includes(s)
        || (r.ip || "").toLowerCase().includes(s)
        || JSON.stringify(r.meta || {}).toLowerCase().includes(s);
  });

  return (
    <div data-testid="admin-compliance-log">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">Compliance Audit Log</div>
          <h2 className="font-serif text-2xl text-[#F5F5F0]">Every sensitive action · IP · user-agent · timestamp</h2>
          <p className="font-body text-[13px] text-[#8A8A82] mt-1 max-w-2xl">
            Legally-defensible trail — read-only. Only whitelisted event families are captured
            to keep the log lean and enforceable.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="audit-action-filter"
            className="bg-black border border-[#C9A961]/30 rounded-sm px-3 py-2 font-body text-sm">
            <option value="">All actions</option>
            <option value="auth.login">Auth · login</option>
            <option value="kyc.completed">KYC completed</option>
            <option value="refund_claim.submitted">Refund submitted</option>
            <option value="admin.refund_approved">Admin refund approved</option>
            <option value="admin.refund_rejected">Admin refund rejected</option>
            <option value="admin.refund_step_advanced">Admin timeline advanced</option>
            <option value="deactivate">Deactivate</option>
            <option value="reactivate">Reactivate</option>
          </select>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter (email, ip, action)"
            data-testid="audit-search"
            className="bg-black border border-[#C9A961]/30 rounded-sm px-3 py-2 font-body text-sm w-56" />
          <button onClick={load} data-testid="audit-refresh"
            className="rounded-sm border border-[#C9A961]/40 text-[#C9A961] px-3 py-2 font-grotesk text-[10px] uppercase tracking-[0.28em]">
            <RefreshCw size={12} className="inline mr-1 -mt-0.5" />Refresh
          </button>
        </div>
      </div>

      {loading ? <div className="font-body text-sm text-[#8A8A82]">Loading…</div> :
       filtered.length === 0 ? <div className="font-body text-sm text-[#8A8A82]">No events.</div> :
       <div className="rounded-sm border border-[#C9A961]/20 overflow-hidden">
         <table className="w-full text-left">
           <thead>
             <tr className="bg-black/60 font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">
               <th className="px-3 py-2">When (UTC)</th>
               <th className="px-3 py-2">Action</th>
               <th className="px-3 py-2">Actor</th>
               <th className="px-3 py-2">IP</th>
               <th className="px-3 py-2">Meta</th>
             </tr>
           </thead>
           <tbody className="font-body text-[12px] text-[#F5F5F0]">
             {filtered.map((r) => (
               <tr key={r.id} data-testid="audit-row" className="border-t border-white/5 hover:bg-black/60">
                 <td className="px-3 py-2 whitespace-nowrap text-[#C9C9C2] tabular-nums">{(r.at || "").slice(0, 19)}</td>
                 <td className="px-3 py-2">
                   <span className="font-grotesk text-[10px] uppercase tracking-[0.28em] px-2 py-0.5 rounded-full border"
                     style={{ color: pillFor(r.action), borderColor: `${pillFor(r.action)}55` }}>
                     {r.action}
                   </span>
                 </td>
                 <td className="px-3 py-2">{r.actor_email || r.user_email || <span className="text-[#8A8A82]">system</span>}</td>
                 <td className="px-3 py-2 tabular-nums text-[#C9C9C2]">{r.ip ? <><MapPin size={10} className="inline mr-1 -mt-0.5 text-[#8A8A82]" />{r.ip}</> : "—"}</td>
                 <td className="px-3 py-2 text-[#8A8A82] max-w-md truncate" title={JSON.stringify(r.meta)}>
                   {r.meta && Object.keys(r.meta).length ? JSON.stringify(r.meta) : ""}
                 </td>
               </tr>
             ))}
           </tbody>
         </table>
       </div>}
    </div>
  );
}
